<script setup lang="ts">
import {
	computed, reactive, ref, 
} from "vue";

import { z } from "zod";

import { client } from "@/api";

import BrokerSelect from "./BrokerSelect.vue";
import ClassASvg from "./ClassASvg.vue";


// Form schema with proper validation
const formSchema = z.object({
	email: z.email("Please enter a valid email address"),
	name: z.string().min(2, "Name must be at least 2 characters"),
	phone: z.string().nullable().default(null),
	tailNumber: z.string().min(1, "Tail number is required"),
	broker: z.string().nullable().default(null),
});

type FormData = z.output<typeof formSchema>;

const formState = reactive<Partial<FormData>>({
	email: "",
	name: "",
	phone: "",
	tailNumber: "",
	broker: "",
});

const toast = useToast();

const isSubmitting = ref(false);
const submitMessage = ref<{ type: "success" | "error"; message: string } | null>(null);
const label = computed(() => {
	return isSubmitting.value ? "Submitting..." : "Unlock Your Benefits";
});

const onSubmit = async (_event: unknown) => {
	try {
		isSubmitting.value = true;
		submitMessage.value = null;
		
		const result = await client.api.v1.events.signup.post({
			email: formState.email!,
			name: formState.name!,
			phone: formState.phone || null,
			tailNumber: formState.tailNumber!,
			broker: formState.broker || null,
			event: "generic",
		});
		
		if (result.data) {
			submitMessage.value = {
				type: "success",
				message: "Thank you for your interest! A Class A representative will be in touch soon.",
			};
			
			// Reset form
			formState.email = "";
			formState.name = "";
			formState.phone = "";
			formState.tailNumber = "";
			formState.broker = "";
		} else {
			toast.add({
				title: "Oh no! Something went wrong",
				description: "Please try again or contact us directly for assistance.",
				color: "error",
			});
		}
	} catch (error) {
		console.error("Submission error:", error);
		submitMessage.value = {
			type: "error",
			message: "Submission failed. Please try again or contact us directly for assistance.",
		};
	} finally {
		isSubmitting.value = false;
	}
};

</script>

<template>
  <div class="min-h-screen flex flex-col justify-between">
    <section class="bg-gray-100 min-h-[50vh] py-12 grow">
      <div class="max-w-5xl mx-auto">
        <div class="flex items-center justify-center mb-12">
          <ClassASvg class="w-60" />
        </div>
        <h3 class="text-2xl lg:text-3xl mb-4 font-serif text-center max-w-3xl mx-auto">
          Elevating your insurance experience with a shared commitment to safety and continuous improvement
        </h3>
        <div class="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 bg-gray-900 p-8 mt-8">
          <h2 class="text-2xl lg:text-4xl font-serif mb-8 text-white">
            Sign Up
          </h2>
          <UForm
            :schema="formSchema"
            :state="formState"
            class="space-y-6"
            @submit="onSubmit"
          >
            <div class="grid md:grid-cols-2 gap-6">
              <UFormField
                label="Full Name"
                name="name"
                required
                :ui="{
                  label: 'text-white',
                }"
              >
                <UInput
                  v-model="formState.name"
                  placeholder="Enter your full name"
                  size="lg"
                  class="w-full"
                />
              </UFormField>
                    
              <UFormField
                label="Email Address"
                name="email"
                required
                :ui="{
                  label: 'text-white',
                }"
              >
                <UInput
                  v-model="formState.email"
                  type="email"
                  placeholder="your.email@example.com"
                  size="lg"
                  class="w-full"
                />
              </UFormField>
            </div>
                  
            <div class="grid md:grid-cols-2 gap-6">
              <UFormField
                label="Phone Number"
                name="phone"
                :ui="{
                  label: 'text-white',
                }"
              >
                <UInput
                  v-model="formState.phone"
                  placeholder="(555) 123-4567"
                  size="lg"
                  class="w-full"
                />
              </UFormField>
                    
              <UFormField
                label="Tail Number"
                name="tailNumber"
                required
                :ui="{
                  label: 'text-white',
                }"
              >
                <UInput
                  v-model="formState.tailNumber"
                  placeholder="N####"
                  size="lg"
                  class="w-full"
                />
              </UFormField>
            </div>
                  
            <UFormField
              label="Brokerage"
              :ui="{
                label: 'text-white',
              }"
            >
              <BrokerSelect
                v-model="formState.broker"
                class="w-full"
              />
            </UFormField>
                  
            <UButton
              type="submit"
              color="primary"
              size="xl"
              :loading="isSubmitting"
              :disabled="isSubmitting"
              :label="label"
              icon="ic-round-airplane-ticket"
              class="mt-4"
            />
          </UForm>
                
          <div
            v-if="submitMessage"
            class="mt-6 p-4 rounded-lg"
            :class="submitMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'"
          >
            <p class="text-center font-medium">
              {{ submitMessage.message }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <div class="grow bg-primary-500 max-h-[500px] lg:h-[30vh] h-60">
      <UContainer class="flex justify-center items-end size-full mx-auto">
        <ClassASvg />
      </UContainer>
    </div>
  </div>
</template>
